import React, { FC, useMemo } from 'react';
import cn from 'classnames';
import { BaseButton, ModalX } from '../../../../../BaseComponents';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../../theme';
import { Seat } from '../../../../types';
import SeatPrice from '../../../SeatPrice/SeatPrice';
import { CdnIcon } from '@shared/ui/CdnIcon';
import Notification from '../../../../../BaseComponents/Notification/Notification';
import SeatAdditionalInfo from '../../../SeatsInfo/SeatAdditionalInfo/SeatAdditionalInfo';
import { useSeatAdditionalInfo } from '../../../SeatsInfo/hooks';
import { SeatComfort } from '@websky/graphql';
import { Money as MoneyType } from '../../../../../types';
import { PriceDetailsItem } from '../../types';

interface Props {
	seats: Seat[];
	totalSeatPrice?: MoneyType;
	priceDetails?: PriceDetailsItem[];

	dataColorNum?: string;
	backgroundColor?: string;

	isSelected?: boolean;
	isFreeSeat?: boolean;

	isOpen: boolean;
	onClose: () => void;
	onSelect: (seat: Seat) => void;
}

const SeatChoose: FC<Props> = ({
	seats,
	totalSeatPrice,
	priceDetails,
	dataColorNum,
	backgroundColor,
	isSelected,
	isFreeSeat,
	isOpen,
	onClose,
	onSelect
}) => {
	const { t } = useTranslation('SeatMap');
	const { SeatChoose: css } = useTheme('SeatMap');

	const { restrictions, permissions, additional, withInfo } = useSeatAdditionalInfo(seats);

	const isDoubleSeat = seats?.length > 1;

	const mainSeat = seats?.[0];
	const service = mainSeat?.service;

	const description = service?.description || '';
	const parameters = service?.parameters || [];

	const showInfo = Boolean(description || parameters.length);
	const showExtendedSeatIcon = mainSeat?.isMoreSpaceForLegs;

	const showPriceDetails = priceDetails?.length > 0;

	const name = useMemo(() => {
		if (isFreeSeat) {
			return t('Free seat');
		}

		if (isDoubleSeat) {
			return t('Double seat');
		}

		return service?.name || '';
	}, [service, isDoubleSeat, isFreeSeat]);

	const onSelectHandler = () => {
		onSelect(seats[0]);
	};

	return (
		<ModalX
			keepMounted={false}
			isOpen={isOpen}
			classes={{
				root: css.modal__root
			}}
			onClose={onClose}
			enableSwipeToClose={false}
			title={<div className={css.title}>{t('Chosen seat')}</div>}
		>
			<div
				className={cn(css.content, {
					[css.economy]: service?.comfort === SeatComfort.Standart,
					[css.preferred]: service?.comfort === SeatComfort.Preferred,
					[css.business]: service?.comfort === SeatComfort.Comfort,
					[css.free]: isFreeSeat
				})}
				data-test-id="seat-choose"
				data-seat-number={mainSeat?.number}
				data-color-num={dataColorNum}
				data-rfisc={service?.rfisc}
			>
				{withInfo && (
					<Notification
						type="context"
						status="warning"
						text={
							<SeatAdditionalInfo
								classes={{
									additionalInfo: css.additionalInfo,
									additionalInfo__title: css.additionalInfo__title
								}}
								restrictions={restrictions}
								permissions={permissions}
								additional={additional}
							/>
						}
					/>
				)}

				<div className={css.seats__info}>
					<div className={css.seats}>
						<div className={css.seats_items}>
							{seats?.map((seat, index) => (
								<div
									key={index}
									className={css.seat}
									style={backgroundColor ? { background: backgroundColor } : null}
								>
									{showExtendedSeatIcon && (
										<CdnIcon className={css.extendedIcon} icon="Seat map/Extra leg room" />
									)}

									{seat.number}
								</div>
							))}
						</div>

						<div className={css.line__vertical} />

						<CdnIcon
							className={css.icon}
							icon={isDoubleSeat ? 'Services/Double seat' : 'Services/Seat Selection'}
						/>

						<div className={css.name}>{name}</div>
					</div>

					<div className={css.line__horizontal} />

					{showInfo && (
						<div className={css.info}>
							{!!description && <div className={css.description}>{description}</div>}

							{!!parameters.length && (
								<div className={css.parameters}>
									{parameters.map(parameter => (
										<div key={parameter.title}>
											{parameter.title}: {parameter.value}
										</div>
									))}
								</div>
							)}
						</div>
					)}
				</div>

				{showPriceDetails && (
					<div className={css.price__details_wrapper}>
						{priceDetails.map(({ label, price }) => (
							<div key={label} className={cn(css.price__details_item, css.price__details_price)}>
								{label} <SeatPrice moneyClassName={css.price__details_price} money={price} />
							</div>
						))}
					</div>
				)}

				{!isFreeSeat && (
					<div className={cn(css.price__total_wrapper, css.price__total_price)}>
						{showPriceDetails && `${t('Total')}:`}
						<SeatPrice moneyClassName={css.price__total_price} money={totalSeatPrice} />
					</div>
				)}

				<BaseButton
					className={css.button}
					variant={isSelected ? 'smallSecondary' : 'smallPrimary'}
					data-test-id="seat-choose-action"
					onClick={onSelectHandler}
				>
					{t(isSelected ? 'Cancel seat' : 'Take this seat')}
				</BaseButton>
			</div>
		</ModalX>
	);
};

export default SeatChoose;
